import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';



import Button from '../../components/ui/Button';
import MetricCard from './components/MetricCard';
import CompletionFunnelChart from './components/CompletionFunnelChart';
import CompanyComparisonChart from './components/CompanyComparisonChart';
import DataQualityWidget from './components/DataQualityWidget';
import TimeSpentAnalysis from './components/TimeSpentAnalysis';
import InsightsPanel from './components/InsightsPanel';
import FilterPanel from './components/FilterPanel';
import SatisfactionRating from './components/SatisfactionRating';

const AnalyticsOverview = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState('month');
  const [selectedCompany, setSelectedCompany] = useState('all');

  const metricsData = [
    {
      title: "Total Registrations",
      value: "1,247",
      change: "+12.5%",
      changeType: "positive",
      icon: "Users",
      iconColor: "bg-primary",
      trend: true
    },
    {
      title: "Completion Rate",
      value: "87.3%",
      change: "+5.2%",
      changeType: "positive",
      icon: "CheckCircle2",
      iconColor: "bg-success",
      trend: true
    },
    {
      title: "Avg. Time to Complete",
      value: "18.5 min",
      change: "-2.3 min",
      changeType: "positive",
      icon: "Clock",
      iconColor: "bg-accent",
      trend: true
    },
    {
      title: "Drop-off Rate",
      value: "12.7%",
      change: "-3.1%",
      changeType: "positive",
      icon: "TrendingDown",
      iconColor: "bg-warning",
      trend: true
    }
  ];

  const funnelData = [
    { stage: "Company Selection", users: 1247 },
    { stage: "Personal Information", users: 1189 },
    { stage: "Employment Details", users: 1142 },
    { stage: "Document Upload", users: 1098 },
    { stage: "Submission Complete", users: 1089 }
  ];

  const companyComparisonData = [
    { month: "Jan", eppme: 145, zetavista: 132, degrey: 128 },
    { month: "Feb", eppme: 168, zetavista: 149, degrey: 142 },
    { month: "Mar", eppme: 192, zetavista: 171, degrey: 165 },
    { month: "Apr", eppme: 215, zetavista: 198, degrey: 187 },
    { month: "May", eppme: 238, zetavista: 224, degrey: 209 },
    { month: "Jun", eppme: 267, zetavista: 251, degrey: 238 }
  ];

  const dataQualityMetrics = [
    { category: "Personal Information", score: 94 },
    { category: "Employment Details", score: 89 },
    { category: "Document Uploads", score: 76 },
    { category: "Contact Information", score: 92 },
    { category: "NYSC Details", score: 88 }
  ];

  const timeSpentData = [
    { name: "Company Selection", minutes: 2.5 },
    { name: "Personal Info", minutes: 5.2 },
    { name: "Employment Details", minutes: 4.8 },
    { name: "Document Upload", minutes: 3.7 },
    { name: "Review & Submit", minutes: 2.3 }
  ];

  const insightsData = [
    {
      type: "success",
      title: "Strong Completion Rate",
      description: "Your completion rate of 87.3% is 15% above industry average for multi-step forms.",
      recommendation: "Maintain current UX patterns and consider documenting best practices."
    },
    {
      type: "warning",
      title: "Document Upload Drop-off",
      description: "4.2% of users abandon the process at document upload stage.",
      recommendation: "Consider adding file size guidance and format examples to reduce confusion."
    },
    {
      type: "info",
      title: "Peak Registration Times",
      description: "Most registrations occur between 10 AM - 2 PM on weekdays.",
      recommendation: "Schedule system maintenance outside these hours to minimize disruption."
    },
    {
      type: "default",
      title: "Mobile Usage Growing",
      description: "Mobile registrations increased by 23% this quarter.",
      recommendation: "Prioritize mobile optimization and consider progressive web app features."
    }
  ];

  const satisfactionRatings = [
    { stars: 5, count: 847 },
    { stars: 4, count: 312 },
    { stars: 3, count: 67 },
    { stars: 2, count: 15 },
    { stars: 1, count: 6 }
  ];

  const handleExport = () => {
    console.log('Exporting analytics data...');
    alert('Analytics report exported successfully!');
  };

  const handleRefresh = () => {
    console.log('Refreshing analytics data...');
    alert('Analytics data refreshed!');
  };

  return (
    <div className="min-h-screen bg-background relative">
      {/* Background Pattern - Better visibility */}
      <div className="fixed inset-0 bg-pattern-grid opacity-15 pointer-events-none"></div>
      {/* Warm Gradient - More visible */}
      <div className="fixed inset-0 gradient-warm opacity-8 pointer-events-none"></div>
      
      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Filter Panel */}
        <div className="mb-6">
          <FilterPanel
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedCompany={selectedCompany}
            setSelectedCompany={setSelectedCompany}
            onExport={handleExport}
            onRefresh={handleRefresh}
          />
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metricsData?.map((metric, index) => (
            <MetricCard key={index} {...metric} />
          ))}
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <CompletionFunnelChart data={funnelData} />
          <CompanyComparisonChart data={companyComparisonData} />
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <DataQualityWidget metrics={dataQualityMetrics} />
          <TimeSpentAnalysis data={timeSpentData} />
          <SatisfactionRating ratings={satisfactionRatings} />
        </div>

        {/* Insights Panel */}
        <div className="mb-8">
          <InsightsPanel insights={insightsData} />
        </div>

        {/* Quick Actions */}
        <div className="bg-card rounded-lg p-6 shadow-brand">
          <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Button
              variant="outline"
              iconName="FileText"
              iconPosition="left"
              fullWidth
              onClick={() => navigate('/admin-dashboard')}
            >
              View Submissions
            </Button>
            <Button
              variant="outline"
              iconName="Users"
              iconPosition="left"
              fullWidth
              onClick={() => navigate('/employee-profile-hub')}
            >
              Employee Profiles
            </Button>
            <Button
              variant="outline"
              iconName="Building2"
              iconPosition="left"
              fullWidth
              onClick={() => navigate('/company-specific-landing-pages')}
            >
              Company Portals
            </Button>
            <Button
              variant="outline"
              iconName="Settings"
              iconPosition="left"
              fullWidth
            >
              Settings
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsOverview;